import { trackEvent } from "@/lib/analytics";
import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "@/lib/router-compat";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import InsightCard from "@/components/insights/InsightCard";
import SEOBreadcrumb from "@/components/SEOBreadcrumb";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageContext";
import { useLocalizedRoute } from "@/hooks/useLocalizedRoute";
import SEO from "@/components/SEO";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mail, CheckCircle2, X } from "lucide-react";
import { streetRetailFirst } from "@/lib/insightsAudience";

type Article = {
  id: string;
  slug: string;
  slug_es: string;
  slug_en: string;
  article_type: string;
  title_es: string;
  title_en: string;
  excerpt_es: string;
  excerpt_en: string;
  cover_image_url: string | null;
  cover_image_alt: string;
  featured_image_alt_es: string;
  featured_image_alt_en: string;
  featured_image_title: string;
  published_at: string;
  category_id: string | null;
};

type Category = {
  id: string;
  slug: string;
  name_es: string;
  name_en: string;
  color: string;
};

interface InsightsProps {
  initialArticles?: Article[];
  initialCategories?: Category[];
}

const Insights = ({ initialArticles = [], initialCategories = [] }: InsightsProps) => {
  const { lang } = useLanguage();
  useLocalizedRoute();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("cat") || "all";

  const [articles, setArticles] = useState<Article[]>(initialArticles);
  const [categories, setCategories] = useState<Category[]>(initialCategories);
  const [loading, setLoading] = useState(initialArticles.length === 0);

  // Newsletter subscription
  const [subEmail, setSubEmail] = useState("");
  const [subLoading, setSubLoading] = useState(false);
  const [subDone, setSubDone] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupDismissed, setPopupDismissed] = useState(false);

  const es = lang === "es";
  const seoTitle = es
    ? "Insights Retail · Artículos y Guías — Retail Lemon"
    : "Retail Insights · Articles & Guides — Retail Lemon";
  const seoDesc = es
    ? "Artículos, análisis y guías tácticas sobre operaciones retail, M&A y estrategia para cadenas, inversores y PYMEs de retail."
    : "Articles, analysis and tactical guides on retail operations, M&A and strategy for retail chains, investors and SMBs.";
  const seoKeywords = es
    ? "insights retail, artículos retail, guías retail, consultoría negocios, M&A retail, operaciones retail"
    : "retail insights, retail articles, retail guides, business consulting, retail M&A, retail operations";
  const seoImageAlt = es
    ? "Insights de Retail — Artículos y guías de consultoría retail"
    : "Retail Insights — Articles and retail consulting guides";

  const insightsJsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: es ? "Insights de Retail" : "Retail Insights",
    description: es
      ? "Artículos y guías tácticas sobre operaciones retail, M&A y estrategia"
      : "Articles and tactical guides on retail operations, M&A and strategy",
    url: "https://retaillemon.com/insights",
    publisher: {
      "@type": "Organization",
      name: "Retail Lemon",
      url: "https://retaillemon.com",
    },
  };

  useEffect(() => {
    // Datos ya servidos desde el loader (SSR): no refetch.
    if (initialArticles.length > 0) return;
    const fetchData = async () => {
      const [articlesRes, catsRes] = await Promise.all([
        supabase
          .from("blog_articles_public")
          .select("id, slug, slug_es, slug_en, article_type, title_es, title_en, excerpt_es, excerpt_en, cover_image_url, cover_image_alt, featured_image_alt_es, featured_image_alt_en, featured_image_title, published_at, category_id")
          .order("published_at", { ascending: false }),
        supabase.from("blog_categories").select("*").order("sort_order"),
      ]);
      setArticles((articlesRes.data as Article[]) || []);
      setCategories((catsRes.data as Category[]) || []);
      setLoading(false);
    };
    fetchData();
  }, []);

  // Show popup after scrolling 40% of page
  useEffect(() => {
    if (subDone || popupDismissed) return;
    const handleScroll = () => {
      const scrollPercent = window.scrollY / (document.body.scrollHeight - window.innerHeight);
      if (scrollPercent > 0.4 && !showPopup) {
        setShowPopup(true);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [showPopup, subDone, popupDismissed]);

  const catMap = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.id, c])),
    [categories]
  );

  const filtered = useMemo(() => {
    const base =
      activeCategory === "all"
        ? articles
        : articles.filter((a) => {
            const cat = a.category_id ? catMap[a.category_id] : null;
            return cat?.slug === activeCategory;
          });
    // Delante, comercio a pie de calle; detrás, corporativo / gran cuenta.
    return streetRetailFirst(base, (a) => ({
      titleEs: a.title_es,
      titleEn: a.title_en,
      categorySlug: a.category_id ? catMap[a.category_id]?.slug : null,
    }));
  }, [articles, activeCategory, catMap]);

  const handleSubscribe = async () => {
    if (!subEmail || !subEmail.includes("@")) {
      toast.error(lang === "es" ? "Introduce un email válido" : "Enter a valid email");
      return;
    }
    setSubLoading(true);
    try {
      const { data: existing } = await supabase
        .from("newsletter_subscribers")
        .select("id")
        .eq("email", subEmail)
        .maybeSingle();

      if (existing) {
        toast.info(lang === "es" ? "Ya estás suscrito a nuestros insights" : "You're already subscribed to our insights");
        setSubDone(true);
        setSubLoading(false);
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        const { error } = await supabase.from("newsletter_subscribers").insert({
          email: subEmail,
          user_id: user.id,
          sections: ["novedades", "tendencias"],
          subscribed: true,
        });
        if (error) throw error;
      } else {
        const { error } = await supabase.from("contact_messages").insert({
          name: "Insights Subscriber",
          email: subEmail,
          message: "Suscripción a Insights newsletter desde la página pública.",
        });
        if (error) throw error;
      }

      trackEvent("newsletter_signup", { form_location: "insights", logged_in: !!user });
      setSubDone(true);
      setShowPopup(false);
      toast.success(lang === "es" ? "¡Suscrito! Recibirás nuestros insights por email" : "Subscribed! You'll receive our insights by email");
    } catch {
      toast.error(lang === "es" ? "Error al suscribirse. Inténtalo de nuevo." : "Subscription error. Please try again.");
    }
    setSubLoading(false);
  };

  const dismissPopup = () => {
    setShowPopup(false);
    setPopupDismissed(true);
  };

  return (
    <div className="min-h-screen bg-background font-body">
      <SEO
        title={seoTitle}
        description={seoDesc}
        path={lang === "es" ? "/insights" : "/en/insights"}
        pathEs="/insights"
        pathEn="/en/insights"
        lang={lang}
        keywords={seoKeywords}
        imageAlt={seoImageAlt}
        jsonLd={insightsJsonLd}
      />
      <Header />

      <SEOBreadcrumb
        items={[
          { label: lang === "es" ? "Inicio" : "Home", href: "/" },
          { label: "Insights" },
        ]}
      />

      <main>
        {/* Hero */}
        <section className="pt-8 pb-12 md:pb-16">
          <section className="container max-w-5xl mx-auto px-4 text-center space-y-4">
            <p className="text-sm uppercase tracking-[0.2em] font-bold">
              {(lang === "es" ? "Análisis · Estrategia · Operaciones" : "Analysis · Strategy · Operations").split(" · ").map((word, i, arr) => (
                <span key={i}>
                  <span className="bg-primary/20 px-1 py-0.5 rounded-xs text-foreground" style={{ boxDecorationBreak: "clone", WebkitBoxDecorationBreak: "clone" }}>{word}</span>
                  {i < arr.length - 1 && <span className="text-muted-foreground mx-1">·</span>}
                </span>
              ))}
            </p>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-[1.08]">
              {lang === "es" ? "Insights y Estrategia Retail" : "Retail Insights & Strategy"}
            </h1>
            <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
              {lang === "es"
                ? "Artículos, análisis y guías tácticas para tomar mejores decisiones en tu negocio retail."
                : "Articles, analysis and tactical guides to make better decisions in your retail business."}
            </p>
          </section>
        </section>

        {/* Category filters */}
        <section aria-label={lang === "es" ? "Filtrar por categoría" : "Filter by category"} className="pb-8">
          <section className="container max-w-5xl mx-auto px-4">
            <div className="flex flex-wrap gap-2 justify-center">
              <button
                aria-label="Todas las categorías"
                onClick={() => setSearchParams({})}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  activeCategory === "all"
                    ? "bg-foreground text-background shadow-md"
                    : "bg-muted text-muted-foreground hover:bg-accent"
                }`}
              >
                {lang === "es" ? "Todos" : "All"}
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSearchParams({ cat: cat.slug })}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                    activeCategory === cat.slug
                      ? "text-white shadow-md"
                      : "bg-muted text-muted-foreground hover:bg-accent"
                  }`}
                  style={activeCategory === cat.slug ? { backgroundColor: cat.color } : {}}
                >
                  {lang === "es" ? cat.name_es : cat.name_en}
                </button>
              ))}
            </div>
          </section>
        </section>

        {/* Articles grid */}
        <section aria-label={lang === "es" ? "Lista de artículos" : "Articles list"} className="pb-20">
          <section className="container max-w-5xl mx-auto px-4">
            {loading ? (
              <div className="grid md:grid-cols-2 gap-8">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="rounded-2xl overflow-hidden border border-border">
                    <Skeleton className="aspect-[16/9]" />
                    <div className="p-6 space-y-3">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-6 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20 space-y-3">
                <p className="text-4xl">📰</p>
                <p className="font-display text-lg text-muted-foreground">
                  {lang === "es" ? "Próximamente nuevos artículos" : "New articles coming soon"}
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-8">
                {filtered.map((article) => {
                  const cat = article.category_id ? catMap[article.category_id] : null;
                  return (
                    <InsightCard
                      key={article.id}
                      slug={article.slug}
                      slugEs={article.slug_es}
                      slugEn={article.slug_en}
                      titleEs={article.title_es}
                      titleEn={article.title_en}
                      excerptEs={article.excerpt_es}
                      excerptEn={article.excerpt_en}
                      coverImage={article.cover_image_url}
                      coverImageAlt={article.cover_image_alt || undefined}
                      coverImageAltEs={article.featured_image_alt_es || undefined}
                      coverImageAltEn={article.featured_image_alt_en || undefined}
                      coverImageTitle={article.featured_image_title || undefined}
                      categoryName={cat ? (lang === "es" ? cat.name_es : cat.name_en) : undefined}
                      categoryColor={cat?.color}
                      publishedAt={article.published_at}
                      articleType={article.article_type as "blog" | "guide" | "premium"}
                    />
                  );
                })}
              </div>
            )}
          </section>
        </section>
      </main>

      {/* Success confirmation after subscribing */}
      {subDone && (
        <section className="pb-16">
          <section className="container max-w-3xl mx-auto px-4">
            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6 md:p-8">
              <div className="flex items-center justify-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-foreground" />
                <p className="font-display font-semibold text-lg text-foreground">
                  {lang === "es" ? "¡Listo! Recibirás nuestros insights directamente en tu bandeja." : "Done! You'll receive our insights directly in your inbox."}
                </p>
              </div>
            </div>
          </section>
        </section>
      )}

      {/* Floating popup (appears on scroll) */}
      {showPopup && !subDone && (
        <div className="fixed bottom-6 right-6 z-50 w-[340px] max-w-[calc(100vw-2rem)] animate-in slide-in-from-bottom-4 fade-in duration-500">
          <div className="rounded-2xl border border-primary/30 bg-background shadow-xl p-5 space-y-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-foreground shrink-0" />
                <p className="font-display font-bold text-sm text-foreground">
                  {lang === "es" ? "No te pierdas nada" : "Don't miss out"}
                </p>
              </div>
              <button
                onClick={dismissPopup}
                className="text-muted-foreground hover:text-foreground transition-colors p-0.5"
               aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              {lang === "es"
                ? "Recibe análisis y estrategias retail directamente en tu email."
                : "Get retail analysis and strategies directly in your inbox."}
            </p>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder={lang === "es" ? "tu@email.com" : "you@email.com"}
                value={subEmail}
                onChange={(e) => setSubEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubscribe()}
                className="flex-1 h-9 text-sm bg-background"
              />
              <Button
                onClick={handleSubscribe}
                disabled={subLoading}
                size="sm"
                className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0 h-9"
              >
                {subLoading ? "..." : "OK"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Insights;
